import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import {
  createOnFarmMatingsOptions,
  type MatingsSourceType,
} from '@/services/on-farm-implant';
import { addGridQueryParams } from '@/utils/query';

type OnFarmImplantFilters = {
  siteId: string;
  accountId?: string;
  searchTerm?: string;
};

export function useOnFarmImplantDataSource(
  sourceType: MatingsSourceType,
  filters: OnFarmImplantFilters,
  setLoading: (loading: boolean) => void,
  onRowsLoaded?: () => void,
) {
  const queryClient = useQueryClient();

  const filtersRef = useRef(filters);
  const setLoadingRef = useRef(setLoading);
  const onRowsLoadedRef = useRef(onRowsLoaded);
  useEffect(() => {
    filtersRef.current = filters;
    setLoadingRef.current = setLoading;
    onRowsLoadedRef.current = onRowsLoaded;
  }, [filters, setLoading, onRowsLoaded]);

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      if (!currentFilters.siteId) {
        params.success({ rowData: [], rowCount: 0 });
        return;
      }

      currentSetLoading(true);

      const searchParams = new URLSearchParams();

      if (currentFilters.searchTerm) {
        searchParams.set('search', currentFilters.searchTerm);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      queryClient
        .fetchQuery(
          createOnFarmMatingsOptions(
            sourceType,
            currentFilters.siteId,
            currentFilters.accountId,
            addGridQueryParams(
              searchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        )
        .then((response) => {
          const startRow = params.request.startRow ?? 0;
          params.success({
            rowData: response.items,
            rowCount: response.hasMore
              ? undefined
              : startRow + response.items.length,
          });
          onRowsLoadedRef.current?.();
          currentSetLoading(false);
        })
        .catch(() => {
          params.fail();
          currentSetLoading(false);
        });
    },
    [sourceType, queryClient],
  );

  const dataSource: IServerSideDatasource = useMemo(
    () => ({ getRows }),
    [getRows],
  );

  return { dataSource };
}
